import * as React from 'react';
import {Slot} from '@radix-ui/react-slot';
import {cva, type VariantProps} from 'class-variance-authority';

import {cn} from '../../shared/tailwind';

const buttonVariants = cva(
	[
		'inline-flex',
		'items-center',
		'justify-center',
		'gap-2',
		'text-center',
		'rounded-md',
		'transition-colors',
		'disabled:pointer-events-none',
		'disabled:opacity-50',
		'[&_svg]:pointer-events-none',
		'[&_svg]:size-4',
		'[&_svg]:shrink-0',
		'py-[11px]',
		'md:py-4',
		'px-[22px]',
		'md:px-[35px]',
		'border-2',
		'rounded-full',
		'font-semibold',
		'text-[15px]',
		'leading-none',
	],
	{
		variants: {
			variant: {
				default:
					'border-primary-15 bg-primary-15 text-primary-95 hover:border-[#0059A1] hover:bg-[#0059A1]',
				defaultDark:
					'border-primary-15 bg-primary-15 text-primary-95 shadow-[0px_3px_3px_0px_rgba(0,24,44,0.13)] hover:border-[#0080E5] hover:bg-[#0080E5]',
				secondary:
					'border-secondary-45 bg-white text-primary-5 hover:border-secondary-25 hover:bg-primary-95',
				secondaryDark:
					'border-[#2B77B4] bg-[#005090] text-primary-95 shadow-[0px_3px_3px_0px_rgba(0,24,44,0.13)] hover:bg-[#045EA6]',
				active: 'border-primary-25 bg-primary-25 text-white',
				disabled: 'border-grey-15 bg-grey-15 text-grey-95',
				success:
					'border-success-60 bg-success-60 text-success-3 hover:border-[#9BCB55] hover:bg-[#9BCB55]',
			},
		},
		defaultVariants: {
			variant: 'default',
		},
	},
);

export interface ButtonProps
	extends
		React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({className, variant, asChild = false, ...props}, ref) => {
		const Comp = asChild ? Slot : 'button';
		return (
			<Comp
				className={cn(buttonVariants({variant, className}))}
				ref={ref}
				{...props}
			/>
		);
	},
);
Button.displayName = 'Button';

export {Button, buttonVariants};
